import { MlTextRecord } from 'src/app/Theme/shared/service/ml-description.service';

export type ShoppingBasketSectionId =
  | 'pageSettings'
  | 'createBasket'
  | 'downloadUploadExcel'
  | 'shipmentAddress'
  | 'breakValue'
  | 'deliveryDate'
  | 'confirmationDate'
  | 'itemOptions'
  | 'contactSupport'
  | 'basketSummary'
  | 'orderInformation'
  | 'transportNotificationBanner';

export type ShoppingBasketItemOptionKey =
  | 'moveToAnotherBasket'
  | 'scheduleDeliveryDate'
  | 'duplicateLine';

export const SHOPPING_BASKET_ITEM_OPTION_KEYS: ShoppingBasketItemOptionKey[] = [
  'moveToAnotherBasket',
  'scheduleDeliveryDate',
  'duplicateLine',
];

export const SHOPPING_BASKET_ITEM_OPTION_LABEL_KEYS: Record<
  ShoppingBasketItemOptionKey,
  string
> = {
  moveToAnotherBasket: 'ADCustomerCustomize.ShoppingBasketItemOptionMoveToAnotherList',
  scheduleDeliveryDate: 'ADCustomerCustomize.ShoppingBasketItemOptionScheduleDeliveryDate',
  duplicateLine: 'ADCustomerCustomize.ShoppingBasketItemOptionDuplicateLine',
};

export type ShoppingBasketEditorLayoutMode = 'desktop' | 'mobile';

/** Toggle sections shown in the desktop layout editor (header banners + toolbar + sidebar). */
export const SHOPPING_BASKET_DESKTOP_TOGGLE_SECTION_IDS: ShoppingBasketSectionId[] = [
  'createBasket',
  'downloadUploadExcel',
  'deliveryDate',
  'breakValue',
  'shipmentAddress',
];

/** Toggle sections shown in the mobile layout editor (header cards + toolbar + checkout). */
export const SHOPPING_BASKET_MOBILE_TOGGLE_SECTION_IDS: ShoppingBasketSectionId[] = [
  'createBasket',
  'downloadUploadExcel',
  'deliveryDate',
  'confirmationDate',
  'breakValue',
  'shipmentAddress',
];

/** Content sections editable in both mobile and desktop layout editors. */
export const SHOPPING_BASKET_SHARED_CONTENT_SECTION_IDS: ShoppingBasketSectionId[] = [
  'contactSupport',
  'basketSummary',
  'orderInformation',
  'transportNotificationBanner',
];

/** @deprecated Use SHOPPING_BASKET_SHARED_CONTENT_SECTION_IDS */
export const SHOPPING_BASKET_DESKTOP_CONTENT_SECTION_IDS = SHOPPING_BASKET_SHARED_CONTENT_SECTION_IDS;

/** All visibility-only toggle sections (union of desktop + mobile editors). */
export const SHOPPING_BASKET_TOGGLE_SECTION_IDS: ShoppingBasketSectionId[] = [
  'createBasket',
  'downloadUploadExcel',
  'deliveryDate',
  'confirmationDate',
  'breakValue',
  'shipmentAddress',
];

/** Toggle sections shown in the admin editor for the given layout mode. */
export function getShoppingBasketEditorToggleSectionIds(
  mode: ShoppingBasketEditorLayoutMode,
): readonly ShoppingBasketSectionId[] {
  return mode === 'mobile'
    ? SHOPPING_BASKET_MOBILE_TOGGLE_SECTION_IDS
    : SHOPPING_BASKET_DESKTOP_TOGGLE_SECTION_IDS;
}

export const SHOPPING_BASKET_SECTION_IDS: ShoppingBasketSectionId[] = [
  'pageSettings',
  ...SHOPPING_BASKET_TOGGLE_SECTION_IDS,
  'itemOptions',
  'contactSupport',
  'basketSummary',
  'orderInformation',
  'transportNotificationBanner',
];

export const SHOPPING_BASKET_SECTION_TITLE_KEYS: Record<ShoppingBasketSectionId, string> = {
  pageSettings: 'ADCustomerCustomize.PageSettings',
  createBasket: 'ADCustomerCustomize.ShoppingBasketSectionCreateBasket',
  downloadUploadExcel: 'ADCustomerCustomize.ShoppingBasketSectionDownloadUploadExcel',
  shipmentAddress: 'ADCustomerCustomize.ShoppingBasketSectionShipmentAddress',
  breakValue: 'ADCustomerCustomize.ShoppingBasketSectionBreakValue',
  deliveryDate: 'ADCustomerCustomize.ShoppingBasketSectionDeliveryDate',
  confirmationDate: 'ADCustomerCustomize.ShoppingBasketSectionConfirmationDate',
  itemOptions: 'ADCustomerCustomize.ShoppingBasketSectionItemOptions',
  contactSupport: 'ADCustomerCustomize.ShoppingBasketSectionContactSupport',
  basketSummary: 'ADCustomerCustomize.ShoppingBasketSectionBasketSummary',
  orderInformation: 'ADCustomerCustomize.ShoppingBasketSectionOrderInformation',
  transportNotificationBanner:
    'ADCustomerCustomize.ShoppingBasketSectionTransportNotificationBanner',
};

export type ShoppingBasketOrderInfoFieldKey =
  | 'productImage'
  | 'searchKey'
  | 'packedPerQuantity'
  | 'minOrderQuantity'
  | 'price'
  | 'totalDiscount'
  | 'priceAfterDiscount'
  | 'quantity'
  | 'unit'
  | 'grossAmount'
  | 'totalDiscountAmount'
  | 'lineTotal';

/** Product-line fields shown in the basket table (excludes the product name/code column). */
export const SHOPPING_BASKET_ORDER_INFO_FIELD_KEYS: ShoppingBasketOrderInfoFieldKey[] = [
  'searchKey',
  'packedPerQuantity',
  'minOrderQuantity',
  'price',
  'totalDiscount',
  'priceAfterDiscount',
  'quantity',
  'unit',
  'grossAmount',
  'totalDiscountAmount',
  'lineTotal',
];

/** Fields rendered as their own table columns (product image stays inside the product cell). */
export const SHOPPING_BASKET_ORDER_INFO_COLUMN_KEYS: ShoppingBasketOrderInfoFieldKey[] = [
  ...SHOPPING_BASKET_ORDER_INFO_FIELD_KEYS,
];

/** When upgrading saved layouts, insert missing fields after these anchors. */
export const SHOPPING_BASKET_ORDER_INFO_FIELD_INSERT_AFTER: Partial<
  Record<ShoppingBasketOrderInfoFieldKey, ShoppingBasketOrderInfoFieldKey>
> = {
  totalDiscount: 'price',
  priceAfterDiscount: 'totalDiscount',
  grossAmount: 'unit',
  totalDiscountAmount: 'grossAmount',
  lineTotal: 'totalDiscountAmount',
};

export const SHOPPING_BASKET_ORDER_INFO_FIELD_TITLE_KEYS: Record<
  ShoppingBasketOrderInfoFieldKey,
  string
> = {
  productImage: 'ADCustomerCustomize.ProductImage',
  searchKey: 'ADCustomerCustomize.ShoppingBasketOrderInfoSearchKey',
  packedPerQuantity: 'ADCustomerCustomize.ShoppingBasketOrderInfoPackedPerQuantity',
  minOrderQuantity: 'ADCustomerCustomize.ShoppingBasketOrderInfoMinOrderQuantity',
  price: 'ADCustomerCustomize.ShoppingBasketOrderInfoPrice',
  totalDiscount: 'ADCustomerCustomize.ShoppingBasketOrderInfoDiscount',
  priceAfterDiscount: 'ADCustomerCustomize.ShoppingBasketOrderInfoUnitPriceAfterDiscount',
  quantity: 'ADCustomerCustomize.ShoppingBasketOrderInfoQuantity',
  unit: 'ADCustomerCustomize.ShoppingBasketOrderInfoUnit',
  grossAmount: 'ADCustomerCustomize.ShoppingBasketOrderInfoGrossAmount',
  totalDiscountAmount: 'ADCustomerCustomize.ShoppingBasketOrderInfoTotalDiscountAmount',
  lineTotal: 'ADCustomerCustomize.ShoppingBasketOrderInfoLineTotal',
};

export interface ShoppingBasketOrderInfoField {
  key: ShoppingBasketOrderInfoFieldKey;
  hidden: boolean;
  titleMl: MlTextRecord;
  /** Product field path feeding this column's value (currently used by the search key column). */
  source?: string;
}

/** Order-info columns whose value can be remapped to another product field. */
export const SHOPPING_BASKET_SOURCE_CONFIGURABLE_FIELD_KEYS: ShoppingBasketOrderInfoFieldKey[] = [
  'searchKey',
];

/** Default product-field path for the basket search key column. */
export const DEFAULT_SHOPPING_BASKET_SEARCH_KEY_SOURCE = 'General_Information.searchKey1';

/** Per-viewport section visibility (desktop and mobile are configured independently). */
export interface ShoppingBasketViewportVisibility {
  createBasketVisible: boolean;
  downloadUploadExcelVisible: boolean;
  shipmentAddressVisible: boolean;
  breakValueVisible: boolean;
  deliveryDateVisible: boolean;
  confirmationDateVisible: boolean;
  itemOptionMoveToAnotherBasketVisible: boolean;
  itemOptionScheduleDeliveryDateVisible: boolean;
  itemOptionDuplicateLineVisible: boolean;
  contactSupportVisible: boolean;
  basketSummaryVisible: boolean;
  orderInformationVisible: boolean;
  transportNotificationBannerVisible: boolean;
}

export type ShoppingBasketSectionVisibilityKey = keyof ShoppingBasketViewportVisibility;

export const DEFAULT_SHOPPING_BASKET_VIEWPORT_VISIBILITY: ShoppingBasketViewportVisibility = {
  createBasketVisible: true,
  downloadUploadExcelVisible: true,
  shipmentAddressVisible: true,
  breakValueVisible: true,
  deliveryDateVisible: true,
  confirmationDateVisible: true,
  itemOptionMoveToAnotherBasketVisible: true,
  itemOptionScheduleDeliveryDateVisible: true,
  itemOptionDuplicateLineVisible: true,
  contactSupportVisible: true,
  basketSummaryVisible: true,
  orderInformationVisible: true,
  transportNotificationBannerVisible: true,
};

export interface ShoppingBasketLayoutConfig {
  pageName: MlTextRecord;
  desktop: ShoppingBasketViewportVisibility;
  mobile: ShoppingBasketViewportVisibility;
  contactSupportTitleMl: MlTextRecord;
  basketSummaryTitleMl: MlTextRecord;
  orderInformationFieldOrder: ShoppingBasketOrderInfoFieldKey[];
  orderInformationFields: ShoppingBasketOrderInfoField[];
  transportNotificationBannerTitleMl: MlTextRecord;
  transportNotificationBannerTextMl: MlTextRecord;
  /** Uploaded image shown as the banner symbol; falls back to the built-in truck icon when empty. */
  transportNotificationBannerSymbolImage: string;
}

export const DEFAULT_SHOPPING_BASKET_LAYOUT_CONFIG: ShoppingBasketLayoutConfig = {
  pageName: {},
  desktop: { ...DEFAULT_SHOPPING_BASKET_VIEWPORT_VISIBILITY },
  mobile: { ...DEFAULT_SHOPPING_BASKET_VIEWPORT_VISIBILITY },
  contactSupportTitleMl: {},
  basketSummaryTitleMl: {},
  orderInformationFieldOrder: [...SHOPPING_BASKET_ORDER_INFO_FIELD_KEYS],
  orderInformationFields: SHOPPING_BASKET_ORDER_INFO_FIELD_KEYS.map((key) => ({
    key,
    hidden: false,
    titleMl: {},
  })),
  transportNotificationBannerTitleMl: {},
  transportNotificationBannerTextMl: {},
  transportNotificationBannerSymbolImage: '',
};

/** Admin preview starts with every section visible. */
export const SHOPPING_BASKET_LAYOUT_PREVIEW_CONFIG: ShoppingBasketLayoutConfig = {
  ...DEFAULT_SHOPPING_BASKET_LAYOUT_CONFIG,
  orderInformationFieldOrder: [...SHOPPING_BASKET_ORDER_INFO_FIELD_KEYS],
  orderInformationFields: DEFAULT_SHOPPING_BASKET_LAYOUT_CONFIG.orderInformationFields.map(
    (field) => ({
      ...field,
      titleMl: { ...field.titleMl },
    }),
  ),
};

export function normalizeShoppingBasketLayoutConfig(payload: unknown): ShoppingBasketLayoutConfig {
  const root =
    payload != null && typeof payload === 'object' && !Array.isArray(payload)
      ? (payload as Record<string, unknown>)
      : {};

  const orderInformationFields = normalizeOrderInformationFields(root);
  const orderInformationFieldOrder = normalizeOrderInformationFieldOrder(
    root['orderInformationFieldOrder'],
    orderInformationFields,
  );

  return {
    pageName: toMlTextRecord(root['pageName']),
    desktop: normalizeShoppingBasketViewportVisibility(root, 'desktop'),
    mobile: normalizeShoppingBasketViewportVisibility(root, 'mobile'),
    contactSupportTitleMl: toMlTextRecord(root['contactSupportTitleMl']),
    basketSummaryTitleMl: toMlTextRecord(root['basketSummaryTitleMl']),
    orderInformationFieldOrder,
    orderInformationFields,
    transportNotificationBannerTitleMl: toMlTextRecord(
      root['transportNotificationBannerTitleMl'],
    ),
    transportNotificationBannerTextMl: toMlTextRecord(root['transportNotificationBannerTextMl']),
    transportNotificationBannerSymbolImage: readText(
      root['transportNotificationBannerSymbolImage'],
    ),
  };
}

function normalizeShoppingBasketViewportVisibility(
  root: Record<string, unknown>,
  mode: ShoppingBasketEditorLayoutMode,
): ShoppingBasketViewportVisibility {
  const nestedRaw = root[mode];
  const nested =
    nestedRaw != null && typeof nestedRaw === 'object' && !Array.isArray(nestedRaw)
      ? (nestedRaw as Record<string, unknown>)
      : {};

  const readVisibility = (
    key: ShoppingBasketSectionVisibilityKey,
    fallback = DEFAULT_SHOPPING_BASKET_VIEWPORT_VISIBILITY[key],
  ): boolean => {
    if (typeof nested[key] === 'boolean') {
      return nested[key] as boolean;
    }
    if (typeof root[key] === 'boolean') {
      return root[key] as boolean;
    }
    return fallback;
  };

  return {
    createBasketVisible: readVisibility('createBasketVisible'),
    downloadUploadExcelVisible: readVisibility('downloadUploadExcelVisible'),
    shipmentAddressVisible: readVisibility('shipmentAddressVisible'),
    breakValueVisible: readVisibility('breakValueVisible'),
    deliveryDateVisible: readVisibility('deliveryDateVisible'),
    confirmationDateVisible: readVisibility('confirmationDateVisible'),
    itemOptionMoveToAnotherBasketVisible: readVisibility('itemOptionMoveToAnotherBasketVisible'),
    itemOptionScheduleDeliveryDateVisible: readVisibility('itemOptionScheduleDeliveryDateVisible'),
    itemOptionDuplicateLineVisible: readVisibility('itemOptionDuplicateLineVisible'),
    contactSupportVisible: readVisibility('contactSupportVisible'),
    basketSummaryVisible: readVisibility('basketSummaryVisible'),
    orderInformationVisible: readVisibility('orderInformationVisible'),
    transportNotificationBannerVisible: readVisibility('transportNotificationBannerVisible'),
  };
}

function normalizeOrderInformationFields(
  root: Record<string, unknown>,
): ShoppingBasketOrderInfoField[] {
  const byKey = new Map<ShoppingBasketOrderInfoFieldKey, ShoppingBasketOrderInfoField>();

  const rawFields = root['orderInformationFields'];
  if (Array.isArray(rawFields)) {
    for (const entry of rawFields) {
      if (entry == null || typeof entry !== 'object' || Array.isArray(entry)) {
        continue;
      }
      const record = entry as Record<string, unknown>;
      const key = String(record['key'] ?? '').trim() as ShoppingBasketOrderInfoFieldKey;
      if (!SHOPPING_BASKET_ORDER_INFO_FIELD_KEYS.includes(key)) {
        continue;
      }
      const sourceRaw = record['source'];
      byKey.set(key, {
        key,
        hidden: readBoolean(record['hidden'], false),
        titleMl: toMlTextRecord(record['titleMl']),
        ...(typeof sourceRaw === 'string' && sourceRaw.trim()
          ? { source: sourceRaw.trim() }
          : {}),
      });
    }
  }

  return SHOPPING_BASKET_ORDER_INFO_FIELD_KEYS.map((key) => {
    const existing = byKey.get(key);
    return (
      existing ?? {
        key,
        hidden: false,
        titleMl: {},
      }
    );
  });
}

function normalizeOrderInformationFieldOrder(
  value: unknown,
  fields: ShoppingBasketOrderInfoField[],
): ShoppingBasketOrderInfoFieldKey[] {
  const valid = new Set(SHOPPING_BASKET_ORDER_INFO_FIELD_KEYS);
  const ordered: ShoppingBasketOrderInfoFieldKey[] = [];

  if (Array.isArray(value)) {
    for (const entry of value) {
      const key = String(entry ?? '').trim() as ShoppingBasketOrderInfoFieldKey;
      if (valid.has(key) && !ordered.includes(key)) {
        ordered.push(key);
      }
    }
  }

  for (const field of fields) {
    if (!ordered.includes(field.key)) {
      ordered.push(field.key);
    }
  }

  for (const key of SHOPPING_BASKET_ORDER_INFO_FIELD_KEYS) {
    insertOrderInfoFieldInOrder(ordered, key);
  }

  return ordered;
}

function insertOrderInfoFieldInOrder(
  ordered: ShoppingBasketOrderInfoFieldKey[],
  key: ShoppingBasketOrderInfoFieldKey,
): void {
  if (ordered.includes(key)) {
    return;
  }

  const anchor = SHOPPING_BASKET_ORDER_INFO_FIELD_INSERT_AFTER[key];
  if (anchor && ordered.includes(anchor)) {
    ordered.splice(ordered.indexOf(anchor) + 1, 0, key);
    return;
  }

  ordered.push(key);
}

function readBoolean(value: unknown, fallback: boolean): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

function readText(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

/**
 * Resolve the search key cell value for a basket line from the configured source.
 * Prefers a full product payload when available (admin preview); otherwise maps the
 * source leaf name onto the flat basket item properties returned by the basket API.
 */
export function resolveShoppingBasketSearchKeyDisplay(
  item: {
    item?: string | null;
    searchKey1?: string | null;
  } | null | undefined,
  source: string | null | undefined,
  product?: unknown,
): string {
  const path = (source ?? '').trim() || DEFAULT_SHOPPING_BASKET_SEARCH_KEY_SOURCE;

  if (product && typeof product === 'object') {
    const fromProduct = resolveNestedProductField(product, path);
    if (fromProduct) {
      return fromProduct;
    }
  }

  const leaf = path.includes('.') ? path.split('.').pop()! : path;
  const record = item as Record<string, unknown> | null | undefined;
  const fromItem = record?.[leaf];
  if (fromItem != null && String(fromItem).trim()) {
    return String(fromItem).trim();
  }

  return '';
}

/** True when the configured search-key source is already present on a flat basket line. */
export function isShoppingBasketSearchKeyResolvedOnItem(
  item: {
    item?: string | null;
    searchKey1?: string | null;
  } | null | undefined,
  source: string | null | undefined,
): boolean {
  return !!resolveShoppingBasketSearchKeyDisplay(item, source);
}

function resolveNestedProductField(product: unknown, path: string): string {
  const value = path.split('.').reduce((acc: unknown, key: string) => {
    if (acc == null || typeof acc !== 'object') {
      return undefined;
    }
    return (acc as Record<string, unknown>)[key];
  }, product);

  if (value == null) {
    return '';
  }
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value).trim();
  }
  return '';
}

export function toMlTextRecord(value: unknown): MlTextRecord {
  if (value == null) {
    return {};
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed ? { en: trimmed } : {};
  }
  if (typeof value === 'object' && !Array.isArray(value)) {
    const record: MlTextRecord = {};
    for (const [key, entry] of Object.entries(value as Record<string, unknown>)) {
      if (typeof entry === 'string' && entry.trim()) {
        record[key] = entry.trim();
      }
    }
    return record;
  }
  return {};
}

export function hasShoppingBasketMlTextContent(record: MlTextRecord | null | undefined): boolean {
  if (!record) {
    return false;
  }
  return Object.values(record).some((value) => typeof value === 'string' && value.trim().length > 0);
}

export function cloneShoppingBasketLayoutConfig(
  config: ShoppingBasketLayoutConfig,
): ShoppingBasketLayoutConfig {
  return {
    ...config,
    pageName: { ...config.pageName },
    desktop: { ...config.desktop },
    mobile: { ...config.mobile },
    contactSupportTitleMl: { ...config.contactSupportTitleMl },
    basketSummaryTitleMl: { ...config.basketSummaryTitleMl },
    transportNotificationBannerTitleMl: { ...config.transportNotificationBannerTitleMl },
    transportNotificationBannerTextMl: { ...config.transportNotificationBannerTextMl },
    transportNotificationBannerSymbolImage: config.transportNotificationBannerSymbolImage || '',
    orderInformationFieldOrder: [...config.orderInformationFieldOrder],
    orderInformationFields: config.orderInformationFields.map((field) => ({
      ...field,
      titleMl: { ...field.titleMl },
      ...(field.source ? { source: field.source } : {}),
    })),
  };
}

export function shoppingBasketSectionVisibleKey(
  sectionId: ShoppingBasketSectionId,
): ShoppingBasketSectionVisibilityKey | null {
  if (sectionId === 'pageSettings') {
    return null;
  }
  return `${sectionId}Visible` as ShoppingBasketSectionVisibilityKey;
}

export function getShoppingBasketViewportVisibility(
  config: ShoppingBasketLayoutConfig,
  mode: ShoppingBasketEditorLayoutMode,
): ShoppingBasketViewportVisibility {
  return config[mode];
}

export function isShoppingBasketSectionVisible(
  config: ShoppingBasketLayoutConfig,
  sectionId: ShoppingBasketSectionId,
  mode: ShoppingBasketEditorLayoutMode,
): boolean {
  const key = shoppingBasketSectionVisibleKey(sectionId);
  if (!key) {
    return true;
  }
  return getShoppingBasketViewportVisibility(config, mode)[key] !== false;
}

export function patchShoppingBasketSectionVisibility(
  config: ShoppingBasketLayoutConfig,
  sectionId: ShoppingBasketSectionId,
  mode: ShoppingBasketEditorLayoutMode,
  visible: boolean,
): ShoppingBasketLayoutConfig {
  const key = shoppingBasketSectionVisibleKey(sectionId);
  if (!key) {
    return config;
  }
  return {
    ...config,
    [mode]: {
      ...config[mode],
      [key]: visible,
    },
  };
}

export function shoppingBasketItemOptionVisibleKey(
  optionKey: ShoppingBasketItemOptionKey,
): keyof ShoppingBasketViewportVisibility {
  switch (optionKey) {
    case 'moveToAnotherBasket':
      return 'itemOptionMoveToAnotherBasketVisible';
    case 'scheduleDeliveryDate':
      return 'itemOptionScheduleDeliveryDateVisible';
    case 'duplicateLine':
      return 'itemOptionDuplicateLineVisible';
  }
}

export function isShoppingBasketItemOptionVisible(
  config: ShoppingBasketLayoutConfig,
  optionKey: ShoppingBasketItemOptionKey,
  mode: ShoppingBasketEditorLayoutMode,
): boolean {
  const key = shoppingBasketItemOptionVisibleKey(optionKey);
  return getShoppingBasketViewportVisibility(config, mode)[key] !== false;
}

export function patchShoppingBasketItemOptionVisibility(
  config: ShoppingBasketLayoutConfig,
  optionKey: ShoppingBasketItemOptionKey,
  mode: ShoppingBasketEditorLayoutMode,
  visible: boolean,
): ShoppingBasketLayoutConfig {
  const key = shoppingBasketItemOptionVisibleKey(optionKey);
  return {
    ...config,
    [mode]: {
      ...config[mode],
      [key]: visible,
    },
  };
}

export function isOrderInfoFieldVisible(
  config: ShoppingBasketLayoutConfig,
  fieldKey: ShoppingBasketOrderInfoFieldKey,
): boolean {
  const field = config.orderInformationFields.find((entry) => entry.key === fieldKey);
  return field ? !field.hidden : true;
}

export function getOrderInfoFieldTitleMl(
  config: ShoppingBasketLayoutConfig,
  fieldKey: ShoppingBasketOrderInfoFieldKey,
): MlTextRecord {
  const field = config.orderInformationFields.find((entry) => entry.key === fieldKey);
  return field?.titleMl ?? {};
}

export function getOrderInfoFieldSource(
  config: ShoppingBasketLayoutConfig,
  fieldKey: ShoppingBasketOrderInfoFieldKey,
): string {
  const field = config.orderInformationFields.find((entry) => entry.key === fieldKey);
  const source = field?.source?.trim();
  if (source) {
    return source;
  }
  return fieldKey === 'searchKey' ? DEFAULT_SHOPPING_BASKET_SEARCH_KEY_SOURCE : '';
}

/** Visible order-info fields that map to basket table columns, in configured display order. */
export function getOrderedVisibleOrderInfoColumns(
  config: ShoppingBasketLayoutConfig,
): ShoppingBasketOrderInfoFieldKey[] {
  const columnKeys = new Set<ShoppingBasketOrderInfoFieldKey>(
    SHOPPING_BASKET_ORDER_INFO_COLUMN_KEYS,
  );
  return config.orderInformationFieldOrder.filter(
    (key) => columnKeys.has(key) && isOrderInfoFieldVisible(config, key),
  );
}
